import React, { useState } from 'react';
import './SalesPage.css';

const SalesPage = () => {
    const [activeSection, setActiveSection] = useState('qualification');
    // Initialize all items as open by default
    const [openItems, setOpenItems] = useState(() => {
        const initialState = {};
        // Set all qualification and objection items to be open initially
        // Qualification: 3 categories with ~4 questions each
        for (let categoryIndex = 0; categoryIndex < 3; categoryIndex++) {
            for (let questionIndex = 0; questionIndex < 6; questionIndex++) {
                initialState[`qual-${categoryIndex}-${questionIndex}`] = true;
            }
        }
        // Objections: 4 categories with ~3 objections each
        for (let categoryIndex = 0; categoryIndex < 4; categoryIndex++) {
            for (let objectionIndex = 0; objectionIndex < 5; objectionIndex++) {
                initialState[`obj-${categoryIndex}-${objectionIndex}`] = true;
            }
        }
        return initialState;
    });

    const toggleItem = (index) => {
        setOpenItems(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const qualificationQuestions = [
        {
            category: "Business Model & Content",
            questions: [
                {
                    question: "What type of content do you plan to stream?",
                    purpose: "Understanding content type helps determine bandwidth, storage, and CDN requirements",
                    followUp: "Live events require different infrastructure than on-demand content",
                    options: ["Movies & TV Shows", "Educational Content", "Live Sports/Events", "Music/Audio", "Corporate Training", "Mixed Content"]
                },
                {
                    question: "What's your target audience size in the first year?",
                    purpose: "Helps determine package selection and scaling requirements",
                    followUp: "Consider both concurrent users and total subscriber base",
                    options: ["Under 1,000 users", "1,000 - 10,000 users", "10,000 - 100,000 users", "100,000+ users"]
                },
                {
                    question: "Which revenue model are you considering?",
                    purpose: "Determines pricing strategy and feature requirements",
                    followUp: "Many successful platforms use hybrid models for diversified revenue",
                    options: ["SVOD (Subscription)", "AVOD (Ad-supported)", "TVOD (Pay-per-view)", "HYBRID (Multiple models)", "Not sure yet"]
                },
                {
                    question: "Do you have existing content or need content creation?",
                    purpose: "Affects storage requirements and launch timeline",
                    followUp: "Content creation timeline impacts go-to-market strategy",
                    options: ["Have existing content library", "Need content creation", "Mix of both", "Content partnerships planned"]
                }
            ]
        },
        {
            category: "Technical Requirements",
            questions: [
                {
                    question: "Which platforms do you want to launch on?",
                    purpose: "Determines app development costs and complexity",
                    followUp: "Web-first approach often provides fastest time-to-market",
                    options: ["Web only", "Web + Mobile", "Web + TV apps", "Full multi-platform", "Not sure yet"]
                },
                {
                    question: "Do you need live streaming capabilities?",
                    purpose: "Live streaming significantly affects infrastructure requirements",
                    followUp: "Live events require specialized encoding and CDN setup",
                    options: ["Yes, essential", "Maybe in the future", "No, only on-demand", "Not sure"]
                },
                {
                    question: "What video quality do you want to offer?",
                    purpose: "Quality level directly impacts bandwidth and storage costs",
                    followUp: "Higher quality increases costs but improves user experience",
                    options: ["SD (480p)", "HD (720p)", "Full HD (1080p)", "4K (2160p)", "Adaptive quality"]
                },
                {
                    question: "Do you have technical team or need managed services?",
                    purpose: "Determines level of support and management needed",
                    followUp: "Managed services reduce complexity but increase costs",
                    options: ["Have technical team", "Need managed services", "Mix of both", "Not sure yet"]
                }
            ]
        },
        {
            category: "Budget & Timeline",
            questions: [
                {
                    question: "What's your budget range for the first year?",
                    purpose: "Helps recommend appropriate package and features",
                    followUp: "Consider both setup costs and ongoing operational expenses",
                    options: ["Under $10K", "$10K - $25K", "$25K - $50K", "$50K - $100K", "$100K+", "Flexible based on ROI"]
                },
                {
                    question: "When do you want to launch your platform?",
                    purpose: "Timeline affects development approach and feature prioritization",
                    followUp: "Faster launches may require phased feature rollouts",
                    options: ["Within 3 months", "3-6 months", "6-12 months", "Over 1 year", "Flexible"]
                },
                {
                    question: "Do you have funding secured or seeking investment?",
                    purpose: "Affects payment terms and growth projections",
                    followUp: "Projections can support funding applications",
                    options: ["Self-funded", "Seeking investment", "Have investors", "Grant funded", "Revenue funded"]
                }
            ]
        }
    ];

    const objectionHandling = [
        {
            category: "Price Objections",
            objections: [
                {
                    objection: "Your pricing seems high compared to other solutions",
                    response: "I understand price is important. Let's break down the value: Our pricing includes enterprise-grade infrastructure, 99.9% uptime, global CDN, and dedicated support. When you factor in the cost of building this yourself - servers, CDN, development team, maintenance - most customers find we're actually 60-70% less expensive than building in-house.",
                    evidence: "Show the calculator's cost breakdown and compare with in-house development costs",
                    followUp: "What specific budget range were you thinking of? Let me show you how our ROI calculator can help justify the investment."
                },
                {
                    objection: "We can build this ourselves for less",
                    response: "Many of our clients initially thought the same thing. Here's what they discovered: Building a streaming platform requires 6-12 months of development, ongoing maintenance, CDN costs, security updates, and scaling infrastructure. Our clients typically save $100K-$500K in the first year alone, plus they launch 6-9 months faster.",
                    evidence: "Share case studies and time-to-market comparisons",
                    followUp: "What's the opportunity cost of delaying your launch by 6-12 months while building internally?"
                },
                {
                    objection: "We need to compare with other vendors first",
                    response: "Absolutely, due diligence is important. To help with your comparison, here are the key questions to ask other vendors: What's included in their pricing? What are the hidden costs? How do they handle scaling? What's their uptime guarantee? Can they provide references from similar businesses? I'm confident our transparent pricing and proven track record will stand out.",
                    evidence: "Provide comparison checklist and competitive analysis",
                    followUp: "What specific features or capabilities are most important in your evaluation?"
                }
            ]
        },
        {
            category: "Feature Objections",
            questions: [
                {
                    objection: "You don't have [specific feature] that we need",
                    response: "I appreciate you bringing that up. Let me understand exactly what you need this feature to accomplish. [Listen to their needs] In many cases, our existing features can achieve the same goal, sometimes even better. If it's truly a gap, our development team regularly adds new features based on client needs.",
                    evidence: "Show roadmap and recent feature additions based on client requests",
                    followUp: "Can you help me understand the business impact if this feature isn't available at launch versus being added in a future update?"
                },
                {
                    objection: "The platform seems too complex for our needs",
                    response: "I understand - simplicity is valuable. The great news is that you only use the features you need. Many clients start with basic streaming and gradually add features as they grow. Our interface is designed so you can start simple and scale complexity as needed. Plus, our support team helps with setup and training.",
                    evidence: "Show simplified setup process and gradual feature adoption",
                    followUp: "What would an ideal simple setup look like for your launch? We can configure it to show only what you need initially."
                },
                {
                    objection: "We need more customization options",
                    response: "Customization is definitely important for brand differentiation. Our platform offers extensive white-labeling and customization options. For unique requirements, we also offer custom development services. Many enterprise clients have specific needs, and we've successfully accommodated requests for custom players, unique workflows, and specialized integrations.",
                    evidence: "Show examples of customized implementations and white-label options",
                    followUp: "What specific customizations are most critical for your brand and user experience?"
                }
            ]
        },
        {
            category: "Technical Objections",
            questions: [
                {
                    objection: "We're concerned about performance and scalability",
                    response: "Performance is absolutely critical for streaming success. Our infrastructure is built on enterprise-grade CDN with 99.9% uptime SLA. We handle clients streaming to millions of users simultaneously. Our auto-scaling ensures your platform performs well whether you have 10 or 10 million viewers. We also provide detailed analytics so you can monitor performance in real-time.",
                    evidence: "Share performance metrics, uptime statistics, and case studies of large-scale deployments",
                    followUp: "What specific performance requirements do you have? Let's run through some scenarios with your expected traffic."
                },
                {
                    objection: "What if your service goes down?",
                    response: "Uptime is our top priority. We maintain 99.9% uptime with redundant infrastructure across multiple data centers. In the rare event of issues, we have 24/7 monitoring and support teams. We also provide SLA guarantees with service credits if we don't meet our uptime commitments. Most importantly, we can show you our public uptime statistics - transparency builds trust.",
                    evidence: "Show uptime statistics, SLA terms, and incident response procedures",
                    followUp: "What level of uptime is critical for your business? Let's discuss SLA options that meet your requirements."
                },
                {
                    objection: "How do we migrate our existing content and users?",
                    response: "Migration is a common concern, and we've made it as smooth as possible. Our team provides dedicated migration support, including content transfer, user account migration, and DNS transition planning. We typically do migrations in phases to minimize disruption. Most clients are surprised how seamless the process is with our migration specialists guiding every step.",
                    evidence: "Share migration timeline, process documentation, and success stories",
                    followUp: "What's your current setup? Let me outline a specific migration plan for your situation."
                }
            ]
        },
        {
            category: "Authority & Decision Making",
            questions: [
                {
                    objection: "I need to discuss this with my team/boss",
                    response: "Of course, this is an important decision that affects your entire organization. To help your discussion be productive, let me provide you with a summary of our conversation, the ROI projections, and answers to common questions your team might have. Would it be helpful if I joined that discussion to answer any technical questions directly?",
                    evidence: "Provide comprehensive proposal with ROI calculations and FAQ",
                    followUp: "What specific concerns or questions do you think your team will have? Let's address those now."
                },
                {
                    objection: "We're not ready to make a decision yet",
                    response: "I completely understand - timing is important. Many successful streaming platforms started their evaluation 6-12 months before they were ready to launch. This gives time for proper planning, content preparation, and team building. What would need to happen for you to feel ready to move forward? Let's create a timeline that works for your situation.",
                    evidence: "Show planning timeline and preparation checklist",
                    followUp: "What's driving your timeline? Are there specific events, funding milestones, or market opportunities you're targeting?"
                },
                {
                    objection: "We need legal/procurement approval",
                    response: "Absolutely, compliance is crucial. We work with legal and procurement teams regularly and can provide all necessary documentation - security certifications, compliance reports, standard contracts, and references. Our legal team can also work directly with yours to address any specific requirements or modifications needed.",
                    evidence: "Provide compliance documentation, security certifications, and legal contacts",
                    followUp: "What specific legal or procurement requirements do you need to satisfy? Let's get that process started now."
                }
            ]
        }
    ];

    return (
        <div className="sales-page">
            <div className="page-header">
                <h1>Sales Qualification & Objection Handling</h1>
                <p className="page-description">
                    Comprehensive guide for qualifying prospects and handling common objections in OTT streaming sales.
                    Use these proven techniques to understand customer needs and address concerns effectively.
                </p>
            </div>

            <div className="sales-container">
                <div className="sales-navigation">
                    <button
                        className={`nav-button ${activeSection === 'qualification' ? 'active' : ''}`}
                        onClick={() => setActiveSection('qualification')}
                    >
                        🎯 Qualification Questions
                    </button>
                    <button
                        className={`nav-button ${activeSection === 'objections' ? 'active' : ''}`}
                        onClick={() => setActiveSection('objections')}
                    >
                        💬 Objection Handling
                    </button>
                </div>

                {activeSection === 'qualification' && (
                    <div className="qualification-section">
                        <div className="section-intro">
                            <h2>Sales Qualification Questions</h2>
                            <p>Use these questions to understand prospect needs and qualify opportunities effectively. Each question includes the purpose and suggested follow-ups.</p>
                        </div>

                        {qualificationQuestions.map((category, categoryIndex) => (
                            <div key={categoryIndex} className="question-category">
                                <h3 className="category-title">{category.category}</h3>
                                <div className="questions-grid">
                                    {category.questions.map((item, questionIndex) => {
                                        const itemIndex = `qual-${categoryIndex}-${questionIndex}`;
                                        const isOpen = openItems[itemIndex];

                                        return (
                                            <div key={questionIndex} className="question-card">
                                                <button
                                                    className={`question-header ${isOpen ? 'open' : ''}`}
                                                    onClick={() => toggleItem(itemIndex)}
                                                >
                                                    <span className="question-text">{item.question}</span>
                                                    <span className="toggle-icon">{isOpen ? '−' : '+'}</span>
                                                </button>
                                                <div className={`question-details ${isOpen ? 'open' : ''}`}>
                                                    <div className="details-content">
                                                        <div className="detail-section">
                                                            <h4>💡 Purpose</h4>
                                                            <p>{item.purpose}</p>
                                                        </div>
                                                        <div className="detail-section">
                                                            <h4>🔄 Follow-up</h4>
                                                            <p>{item.followUp}</p>
                                                        </div>
                                                        <div className="detail-section">
                                                            <h4>📋 Common Responses</h4>
                                                            <ul className="options-list">
                                                                {item.options.map((option, optionIndex) => (
                                                                    <li key={optionIndex}>{option}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {activeSection === 'objections' && (
                    <div className="objections-section">
                        <div className="section-intro">
                            <h2>Objection Handling Guide</h2>
                            <p>Common objections and proven responses. Each includes supporting evidence and follow-up strategies to move the conversation forward.</p>
                        </div>

                        {objectionHandling.map((category, categoryIndex) => (
                            <div key={categoryIndex} className="objection-category">
                                <h3 className="category-title">{category.category}</h3>
                                <div className="objections-list">
                                    {category.objections?.map((item, objectionIndex) => {
                                        const itemIndex = `obj-${categoryIndex}-${objectionIndex}`;
                                        const isOpen = openItems[itemIndex];

                                        return (
                                            <div key={objectionIndex} className="objection-card">
                                                <button
                                                    className={`objection-header ${isOpen ? 'open' : ''}`}
                                                    onClick={() => toggleItem(itemIndex)}
                                                >
                                                    <span className="objection-text">"{item.objection}"</span>
                                                    <span className="toggle-icon">{isOpen ? '−' : '+'}</span>
                                                </button>
                                                <div className={`objection-details ${isOpen ? 'open' : ''}`}>
                                                    <div className="details-content">
                                                        <div className="detail-section">
                                                            <h4>💬 Response</h4>
                                                            <p>{item.response}</p>
                                                        </div>
                                                        <div className="detail-section">
                                                            <h4>📊 Supporting Evidence</h4>
                                                            <p>{item.evidence}</p>
                                                        </div>
                                                        <div className="detail-section">
                                                            <h4>➡️ Follow-up</h4>
                                                            <p>{item.followUp}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }) || category.questions?.map((item, questionIndex) => {
                                        const itemIndex = `obj-${categoryIndex}-${questionIndex}`;
                                        const isOpen = openItems[itemIndex];

                                        return (
                                            <div key={questionIndex} className="objection-card">
                                                <button
                                                    className={`objection-header ${isOpen ? 'open' : ''}`}
                                                    onClick={() => toggleItem(itemIndex)}
                                                >
                                                    <span className="objection-text">"{item.objection}"</span>
                                                    <span className="toggle-icon">{isOpen ? '−' : '+'}</span>
                                                </button>
                                                <div className={`objection-details ${isOpen ? 'open' : ''}`}>
                                                    <div className="details-content">
                                                        <div className="detail-section">
                                                            <h4>💬 Response</h4>
                                                            <p>{item.response}</p>
                                                        </div>
                                                        <div className="detail-section">
                                                            <h4>📊 Supporting Evidence</h4>
                                                            <p>{item.evidence}</p>
                                                        </div>
                                                        <div className="detail-section">
                                                            <h4>➡️ Follow-up</h4>
                                                            <p>{item.followUp}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="sales-footer">
                    <div className="footer-tips">
                        <h4>🎯 Sales Tips</h4>
                        <ul>
                            <li><strong>Listen First:</strong> Understand their specific needs before presenting solutions</li>
                            <li><strong>Use Calculator:</strong> Show concrete ROI and cost projections for their scenario</li>
                            <li><strong>Address Concerns:</strong> Don't avoid objections - address them directly with evidence</li>
                            <li><strong>Create Urgency:</strong> Highlight opportunity costs of delaying their streaming launch</li>
                            <li><strong>Follow Up:</strong> Provide detailed proposals and maintain regular contact</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SalesPage;
